/**
 * HMACSHA256签名工具类
 */
import axios from 'axios'

// 创建一个专用的axios实例，避免与项目中已有的axios配置冲突
const pythonAxios = axios.create({
  timeout: 30000, // 默认超时时间30秒
  headers: {
    'Content-Type': 'application/json'
  }
});

class HmacUtils {
  /**
   * 生成当前时间戳（秒）
   * @returns {number} 时间戳
   */
  static generateTimestamp() {
    return Math.floor(Date.now() / 1000);
  }

  /**
   * 计算HMACSHA256签名并使用Base64编码（与后端保持一致）
   * @param {string} data 待签名的数据 (格式: appId.timestamp)
   * @param {string} key 密钥
   * @returns {string} Base64编码的签名结果
   */
  static async calculateHmacSHA256(data, key) {
    // 检测是否支持window.crypto.subtle
    if (window.crypto && window.crypto.subtle) {
      try {
        // 将密钥和数据转换为Uint8Array
        const encoder = new TextEncoder();
        const keyBytes = encoder.encode(key);
        const dataBytes = encoder.encode(data);
        
        // 导入密钥
        const cryptoKey = await window.crypto.subtle.importKey(
          'raw',
          keyBytes,
          { name: 'HMAC', hash: 'SHA-256' },
          false,
          ['sign']
        );
        
        // 计算签名
        const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, dataBytes);
        
        // 将签名转换为Base64编码（与后端保持一致）
        return this.arrayBufferToBase64(signature);
      } catch (error) {
        console.warn('window.crypto.subtle计算签名失败，尝试使用CryptoJS替代:', error);
        // 计算签名失败，回退到CryptoJS
        return this.calculateHmacSHA256WithCryptoJS(data, key);
      }
    } else {
      // 不支持window.crypto.subtle，使用CryptoJS
      return this.calculateHmacSHA256WithCryptoJS(data, key);
    }
  }

  /**
   * 使用CryptoJS计算HMACSHA256签名（兼容方案）
   * @param {string} data 待签名的数据
   * @param {string} key 密钥
   * @returns {string} Base64编码的签名结果
   */
  static calculateHmacSHA256WithCryptoJS(data, key) {
    // 确保CryptoJS可用
    if (typeof CryptoJS !== 'undefined') {
      // 使用CryptoJS计算HMAC-SHA256签名
      const hash = CryptoJS.HmacSHA256(data, key);
      // 转换为Base64编码
      return hash.toString(CryptoJS.enc.Base64);
    } else {
      console.error('CryptoJS不可用，无法计算HMACSHA256签名');
      throw new Error('CryptoJS不可用，无法计算HMACSHA256签名');
    }
  }

  /**
   * 将ArrayBuffer转换为Base64字符串
   * @param {ArrayBuffer} buffer 待转换的ArrayBuffer
   * @returns {string} Base64编码的字符串
   */
  static arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  /**
   * 生成请求参数（包含签名）
   * @param {string} appId 应用ID
   * @param {string} key 密钥
   * @returns {Object} 包含签名和时间戳的参数对象
   */
  static async generateSignedParams(appId, key) {
    // 生成时间戳
    const timestamp = this.generateTimestamp();
    
    // 按照后端格式拼接数据: appId.timestamp
    const dataToSign = `${appId}.${timestamp}`;
    
    // 计算签名
    const signature = await this.calculateHmacSHA256(dataToSign, key);
    
    console.log('timestamp:', timestamp);
    console.log('签名:', signature);
    // 返回签名和时间戳
    return {
      timestamp,
      signature
    };
  }

  /**
   * 发送带签名的请求
   * @param {string} url 请求地址
   * @param {Object} data 业务数据
   * @param {string} appId 应用ID
   * @param {string} key 密钥
   * @param {string} method 请求方法，默认POST
   * @returns {Promise} 请求结果
   */
  static async sendSignedRequest(url, data, appId, key, method = 'POST') {
    try {
      // 生成带签名的参数
      const params = await this.generateSignedParams('1034602592283309', 'T5XWHmT5AX5R9R4U');
      
      // 发送请求 - 使用专用的pythonAxios实例避免与项目中已有axios冲突
      const response = await pythonAxios({
        url,
        method,
        headers: {
          'Content-Type': 'application/json',
          'token': params.signature,
          'timestamp': params.timestamp,
        },
        data: data
      });
      
      return response.data;
    } catch (error) {
      console.error('请求发送失败:', error);
      // 提供更友好的错误信息
      if (error.response) {
        // 服务器返回了错误状态码
        throw new Error(`请求失败: ${error.response.status} ${error.response.statusText}`);
      } else if (error.request) {
        // 请求已发送但没有收到响应
        throw new Error('网络错误，请检查您的网络连接');
      } else {
        // 设置请求时发生错误
        throw new Error(`请求配置错误: ${error.message}`);
      }
    }
  }
  
  /**
   * 下载文件（带签名认证）
   * @param {Object} v 包含源文件和目标文件信息的对象
   * @param {string} v.srcFile 源文件路径
   * @param {string} v.targetFile 目标文件名
   * @param {string} appId 应用ID
   * @param {string} key 密钥
   * @returns {Promise} 下载结果
   */
  static async downloadFile(v, appId = '1034602592283309', key = 'T5XWHmT5AX5R9R4U') {
    try {
      // 生成带签名的参数
      const params = await this.generateSignedParams(appId, key);
      
      // 构建请求URL
      const url = `${window.PUBLICPYTHON}/api/File?SrcFile=${encodeURIComponent(v.srcFile)}&TargetFile=${encodeURIComponent(v.targetFile)}`;
      
      // 创建一个新的XMLHttpRequest对象进行文件下载
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        
        // 设置请求头
        xhr.setRequestHeader('token', params.signature);
        xhr.setRequestHeader('timestamp', params.timestamp);
        
        // 设置响应类型为blob，用于二进制文件
        xhr.responseType = 'blob';
        
        // 请求成功回调
        xhr.onload = function() {
          if (xhr.status === 200) {
            // 创建一个临时的URL用于下载
            const blob = new Blob([xhr.response], { type: xhr.getResponseHeader('Content-Type') || 'application/octet-stream' });
            const downloadUrl = URL.createObjectURL(blob);
            
            // 创建一个临时的a标签用于触发下载
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = v.targetFile;
            document.body.appendChild(link);
            link.click();
            
            // 清理临时对象
            document.body.removeChild(link);
            URL.revokeObjectURL(downloadUrl);
            
            resolve({ success: true, message: '文件下载成功' });
          } else {
            reject(new Error(`文件下载失败: ${xhr.status} ${xhr.statusText}`));
          }
        };
        
        // 请求错误回调
        xhr.onerror = function() {
          reject(new Error('网络错误，请检查您的网络连接'));
        };
        
        // 发送请求
        xhr.send();
      });
    } catch (error) {
      console.error('文件下载请求发送失败:', error);
      throw error;
    }
  }
}

export default HmacUtils;


// WEBPACK FOOTER //
// ./src/tools/hmac-utils.js